import { useState } from "react";
import { ArrowLeft, Bookmark, BookmarkCheck, Share2, ExternalLink, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { OpportunityApplyModal } from "@/components/buildOpportunities/OpportunityApplyModal";

// Wave 1 — External Opportunity detail (formerly "NJM Opportunity"/"NJM Connection"/
// "Community" — collapsed into ONE label per the approved amendment). Primary action is
// ALWAYS "Apply on Listing" (opens source_url) — the old internal kind="application" flow is
// gone from this tab entirely. "Ask NJM to help" (kind="support_request") is secondary and
// ONLY ever shown here, on the detail view — never on the card. Driven purely by the unified
// card DTO's own fields (no extra fetch — see opportunities_unified.py's _external_to_card).
export function UnifiedOpportunityDetail({ opportunity: o, onClose, onToggleSave, onGoToMyWork, onSent }) {
  const [applying, setApplying] = useState(false);
  if (!o) return null;
  const hasSupportRequest = !!o.my_application_status;

  const share = () => {
    navigator.clipboard?.writeText(`${window.location.origin}${o.detail_route}`).then(() => toast.success("Link copied"));
  };

  return (
    <>
      <div className="hidden lg:block fixed inset-0 z-40 bg-ink/50" onClick={onClose} data-testid="opportunity-detail-backdrop" />
      <div className="fixed inset-0 z-50 lg:inset-y-0 lg:right-0 lg:left-auto lg:w-[560px] bg-paper shadow-2xl flex flex-col border-l border-hairline" data-testid="opportunity-detail-panel">
        <div className="shrink-0 border-b border-hairline px-5 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={onClose} data-testid="opportunity-detail-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink transition-colors">
            <ArrowLeft className="h-4 w-4" /> Opportunities
          </button>
          <button onClick={onClose} className="lg:hidden text-ink-soft hover:text-ink"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          <span className="inline-flex items-center rounded-[4px] px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-[0.12em] bg-surface2 text-ink-soft">External Opportunity</span>
          <div>
            <h2 className="font-mont text-[22px] sm:text-2xl font-extrabold text-ink leading-[1.2] tracking-tight" data-testid="opportunity-detail-title">{o.title}</h2>
            <p className="mt-1.5 text-sm font-medium text-ink-soft">{o.organisation}{[o.city, o.location_type].filter(Boolean).length ? ` · ${[o.city, o.location_type].filter(Boolean).join(" · ")}` : ""}</p>
          </div>

          {o.timeline && (
            <div className="rounded-[9px] border border-hairline bg-surface2/40 p-3">
              <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-faint">Timeline / Deadline</div>
              <div className="text-[12.5px] font-bold text-ink mt-1">{o.timeline}</div>
            </div>
          )}

          {o.summary && (
            <Section title="About the work">{o.summary}</Section>
          )}

          <div className="flex items-center gap-1.5 flex-wrap">
            {(o.discipline || []).map((d) => <Chip key={d}>{d}</Chip>)}
            {(o.tags || []).map((t) => <Chip key={t}>{t}</Chip>)}
          </div>
        </div>

        <div className="shrink-0 border-t border-hairline p-5 space-y-3">
          <div className="flex items-center gap-4">
            <button data-testid="opportunity-detail-save" onClick={() => onToggleSave(o)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-soft hover:text-ink transition-colors">
              {o.is_saved ? <BookmarkCheck className="h-4 w-4 fill-brand text-ink" /> : <Bookmark className="h-4 w-4" />} {o.is_saved ? "Saved" : "Save"}
            </button>
            <button data-testid="opportunity-detail-share" onClick={share} className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-soft hover:text-ink transition-colors">
              <Share2 className="h-4 w-4" /> Copy link
            </button>
          </div>
          <div className="flex flex-col gap-2.5">
            {o.source_url_valid && o.source_url && (
              <a data-testid="opportunity-detail-primary" href={o.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 w-full rounded-[8px] bg-brand h-12 text-sm font-bold text-ink hover:bg-brand/90 transition-colors">
                Apply on Listing <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {hasSupportRequest ? (
              <div data-testid="opportunity-detail-secondary-status" className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-ink-soft">
                <CheckCircle2 className="h-4 w-4" /> Support requested
              </div>
            ) : (
              <button data-testid="opportunity-detail-secondary" onClick={() => setApplying(true)} className="w-full rounded-[8px] border border-ink h-[46px] text-xs font-bold text-ink hover:bg-ink hover:text-paper transition-colors">
                Ask NJM to help
              </button>
            )}
          </div>
        </div>
      </div>

      {applying && (
        <OpportunityApplyModal
          opportunity={o} kind="support_request"
          onClose={() => setApplying(false)}
          onSent={() => { setApplying(false); onSent?.(); }}
          onGoToMyWork={onGoToMyWork}
        />
      )}
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-faint mb-1.5">{title}</div>
      <p className="text-[13.5px] text-ink-soft leading-relaxed whitespace-pre-wrap">{children}</p>
    </div>
  );
}

function Chip({ children }) {
  return <span className="rounded-full bg-surface2 px-2.5 py-1 text-[9.5px] font-semibold uppercase tracking-wide text-ink-soft">{children}</span>;
}
